import { z } from "zod";

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "expected YYYY-MM-DD");

/** How many children one booking may carry. Beyond this the supplier wants multiple rooms. */
export const MAX_STAY_CHILDREN = 4;
/** A child is under 18; the supplier bands prices within that range. */
export const MAX_STAY_CHILD_AGE = 17;

const staysChildAges = z
  .array(z.coerce.number().int().min(0).max(MAX_STAY_CHILD_AGE))
  .max(MAX_STAY_CHILDREN)
  .default([]);

export const staySearchQuerySchema = z
  .object({
    citySlug: z.string().min(1),
    checkin: isoDate,
    checkout: isoDate,
    adults: z.coerce.number().int().min(1).max(10),
    /**
     * One entry per child, holding that child's AGE, not a count.
     *
     * The supplier prices by age and the difference is not marginal: for the
     * same room and dates, two adults plus a two-year-old came back at 10,873
     * MAD across 72 rates, while two adults plus a fifteen-year-old came back
     * at 51,566 MAD across 3 (live sandbox, 2026-08-13). A guessed age would
     * quote the first family and hand the second a bill nearly five times what
     * we showed them, so the age is required rather than assumed.
     */
    childAges: staysChildAges,
    /** Multi-room needs per-occupancy aggregation that is not written yet. */
    rooms: z.coerce.number().int().min(1).max(1).default(1),
    currency: z.string().length(3).default("MAD"),
    guestNationality: z.string().length(2).default("MA"),
  })
  .refine((v) => v.checkout > v.checkin, {
    message: "checkout must be after checkin",
    path: ["checkout"],
  });

export type StaySearchQuery = z.infer<typeof staySearchQuerySchema>;

/**
 * Live pricing for a set of curated properties. The id cap guards against a
 * runaway caller: the Phase 1 catalogue is 100 properties, and the supplier
 * bills on an unreasonable look-to-book ratio.
 */
export const stayRatesRequestSchema = z
  .object({
    supplierHotelIds: z.array(z.string().min(1)).min(1).max(200),
    checkin: isoDate,
    checkout: isoDate,
    adults: z.coerce.number().int().min(1).max(10),
    /** See staySearchQuerySchema: one age per child, not a count. */
    childAges: staysChildAges,
    /** See staySearchQuerySchema: multi-room is still Phase 2. */
    rooms: z.coerce.number().int().min(1).max(1).default(1),
    currency: z.string().length(3).default("MAD"),
    guestNationality: z.string().length(2).default("MA"),
  })
  .refine((v) => v.checkout > v.checkin, {
    message: "checkout must be after checkin",
    path: ["checkout"],
  });

export type StayRatesRequest = z.infer<typeof stayRatesRequestSchema>;

/** What the guest is charged if they cancel after `cutoffIso`. */
export const stayCancellationStepSchema = z.object({
  cutoffIso: z.string(),
  chargeAmount: z.number(),
  currency: z.string(),
});

export const stayCancellationSchema = z.object({
  refundable: z.boolean(),
  /** Deadline before which nothing is charged. Null when no free window exists. */
  freeUntilIso: z.string().nullable(),
  steps: z.array(stayCancellationStepSchema),
});

export const stayTaxSchema = z.object({
  description: z.string(),
  amount: z.number(),
});

/**
 * The room facts shown on a room card, already in the guest's locale.
 *
 * Every string here is the SUPPLIER's word, translated by them — we never
 * translate these ourselves, so a locale with nothing stored yields an empty
 * list rather than an English one.
 */
export const stayRoomFactsSchema = z.object({
  /**
   * The room's name in the requested locale, or null when the content refresh
   * has not read this property in it. The card falls back to the rate's own
   * English name, which is all the rates endpoint can give.
   */
  name: z.string().nullable(),
  /** Floor area in `sizeUnit`. Null when the supplier publishes none. */
  sizeSquare: z.number().positive().nullable(),
  /** The supplier's unit, e.g. "sqm". Repeated, never converted. */
  sizeUnit: z.string().nullable(),
  /** Heads this room sleeps, from published content rather than a refusal. */
  sleeps: z.number().int().positive().nullable(),
  /** Beds summed across bed types. Null when none are published. */
  bedCount: z.number().int().positive().nullable(),
  /** The first few amenities, in the supplier's own order. */
  amenities: z.array(z.string()),
  /** Total published, so a card can say "+38" without holding all of them. */
  amenityCount: z.number().int().nonnegative(),
});

export type StayRoomFacts = z.infer<typeof stayRoomFactsSchema>;

export const stayRateSchema = z.object({
  /**
   * Booking identifier. Lives on the supplier's ROOM TYPE, not the rate.
   *
   * Valid only for the search that minted it. Two identical searches a second
   * apart returned the same 20 rooms at the same prices with 20 different
   * offerIds AND different rateIds (live sandbox, 2026-08-13). Never persist
   * one, never put one in a URL, never match on one across a page load: use
   * `rateKey` to identify a rate, and take the offerId from the search you are
   * about to book with.
   */
  offerId: z.string(),
  rateId: z.string().nullable(),
  /**
   * Stable identity for a rate across searches: room, board, and whether it
   * can be cancelled. Exactly the grouping `dedupeRooms` collapses on, so it
   * is unique within a property's room list by construction.
   */
  rateKey: z.string(),
  roomName: z.string(),
  /**
   * Most guests this room may be sold to, straight from the supplier's
   * `maxOccupancy`. Null when they did not say.
   *
   * Exists so an empty search can be explained instead of blamed on the dates.
   * A party of five on Villa Jannah for 18 to 21 October was told "No rooms
   * for these dates. Try different dates." while the supplier was selling
   * those exact dates at two, three and four adults. The dates were never the
   * problem and changing them could not have helped.
   */
  maxOccupancy: z.number().int().positive().nullable().default(null),
  /**
   * The supplier's id for the ROOM TYPE this rate sells, from `roomMapping`.
   *
   * Only a join key, never shown. It is what lets a rate find its photos: a
   * rate carries a name and a price and no imagery, and matching on the name
   * cannot work — two rates on one room differ only by board, and suppliers
   * name the same room differently. Null when the supplier did not map it.
   */
  mappedRoomId: z.string().nullable().optional(),
  /**
   * Photos of this room type, resolved from the property's stored content by
   * `mappedRoomId`. Empty when the supplier published none, which is common
   * enough that every surface must read well with no image at all.
   */
  images: z.array(z.string()).optional(),
  /**
   * What the supplier publishes ABOUT the room — size, occupancy, beds and
   * amenities — resolved into the locale of the request. Sibling of `images`
   * above, filled from the same stored content by the same `mappedRoomId`.
   *
   * Null whenever the join could not be made: no room mapping on the search,
   * no stored facts for the property, or a locale the content refresh has not
   * reached. The card drops the facts line in that case, which is how it read
   * before this existed.
   */
  facts: stayRoomFactsSchema.nullable().optional(),
  boardName: z.string().nullable(),
  /**
   * The supplier's board code — RO, BI, HB, FB, AI and so on. A closed set,
   * so the client names it in the guest's own locale; `boardName` above is
   * the supplier's English and is only the fallback for an unknown code.
   */
  boardType: z.string().nullable().optional(),
  /** What we charge the guest, margin already included. */
  sellTotal: z.number(),
  /** Supplier net (sellTotal - commission). Server-side only, never serialised. */
  netTotal: z.number(),
  /** The hotel's public price floor for this rate. Server-side only. */
  sspTotal: z.number().nullable(),
  /** Currency of sspTotal. Compared against `currency` before the floor is trusted. */
  sspCurrency: z.string().nullable(),
  /**
   * Taxes the supplier flagged `included: false`. The guest pays these at the
   * property ON TOP of sellTotal, so they must be displayed wherever a price is.
   */
  taxesAtProperty: z.array(stayTaxSchema),
  taxesAtPropertyTotal: z.number(),
  currency: z.string(),
  cancellation: stayCancellationSchema,
});

export const stayHotelSchema = z.object({
  hotelId: z.string(),
  name: z.string(),
  citySlug: z.string(),
  stars: z.number().nullable(),
  thumbnail: z.string().nullable(),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
});

export type StayCancellationStep = z.infer<typeof stayCancellationStepSchema>;
export type StayCancellation = z.infer<typeof stayCancellationSchema>;
export type StayTax = z.infer<typeof stayTaxSchema>;
export type StayRate = z.infer<typeof stayRateSchema>;

/**
 * The one definition of a rate's stable identity. Lives in shared because the
 * server stamps it on every rate and the web checkout matches on it; two
 * copies drifting apart would silently strand a guest on "we could not find
 * this room".
 *
 * Plain text rather than a hash: it is information already printed on the
 * page, it survives percent-encoding in a query string, and it cannot collide
 * the way a truncated hash could.
 */
export function stayRateKey(rate: {
  roomName: string;
  boardName: string | null;
  cancellation: { refundable: boolean };
}): string {
  return [
    rate.roomName.trim().toLowerCase(),
    (rate.boardName ?? "").trim().toLowerCase(),
    rate.cancellation.refundable ? "refundable" : "non-refundable",
  ].join("|");
}
export type StayHotel = z.infer<typeof stayHotelSchema>;
